import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dumbbell, 
  HeartPulse, 
  Users, 
  Target, 
  Activity, 
  Sparkles,
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  Check,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Facebook,
  Video,
  Menu,
  X
} from 'lucide-react';
import { gymInfo, services, testimonials, pricingPlans, location, galleryImages, socialMedia, gymRating, bonusOptions, otherConcepts } from '../mock';
import logo from '../assets/logo.png';

const iconMap = {
  dumbbell: Dumbbell,
  'heart-pulse': HeartPulse,
  users: Users,
  target: Target,
  activity: Activity,
  sparkles: Sparkles,
  instagram: Instagram,
  facebook: Facebook,
  video: Video
};

const Home = () => {
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const nextGalleryImage = () => {
    setCurrentGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevGalleryImage = () => {
    setCurrentGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-zinc-950/95 backdrop-blur-sm z-50 border-b border-zinc-800">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <img src={logo} alt="Infinity Gym S.L." className="h-12 md:h-14 w-auto object-contain" />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <button onClick={() => scrollToSection('inicio')} className="hover:text-emerald-500 transition-colors">Inicio</button>
            <button onClick={() => scrollToSection('sobre')} className="hover:text-emerald-500 transition-colors">Sobre Nosotros</button>
            <button onClick={() => scrollToSection('servicios')} className="hover:text-emerald-500 transition-colors">Servicios</button>
            <button onClick={() => scrollToSection('tarifas')} className="hover:text-emerald-500 transition-colors">Tarifas</button>
            <a href="https://n8n.rivillos.es/webhook/f46f5994-1c0d-46b4-b44a-ac9a6a4ef9e7/chat" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-500 transition-colors">Contacto</a>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden bg-zinc-900 border-t border-zinc-800">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <button onClick={() => scrollToSection('inicio')} className="text-left hover:text-emerald-500 transition-colors">Inicio</button>
              <button onClick={() => scrollToSection('sobre')} className="text-left hover:text-emerald-500 transition-colors">Sobre Nosotros</button>
              <button onClick={() => scrollToSection('servicios')} className="text-left hover:text-emerald-500 transition-colors">Servicios</button>
              <button onClick={() => scrollToSection('tarifas')} className="text-left hover:text-emerald-500 transition-colors">Tarifas</button>
              <a href="https://n8n.rivillos.es/webhook/f46f5994-1c0d-46b4-b44a-ac9a6a4ef9e7/chat" target="_blank" rel="noopener noreferrer" className="text-left hover:text-emerald-500 transition-colors">Contacto</a>
            </div>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section id="inicio" className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50 text-sm px-4 py-2">
              Bienvenido a tu nueva comunidad fitness
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
              {gymInfo.name}
            </h1>
            <p className="text-3xl md:text-4xl text-emerald-500 font-semibold">
              {gymInfo.slogan}
            </p>
            <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              {gymInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-emerald-600 text-emerald-500 hover:bg-emerald-600/10 px-8 py-6 text-lg transition-all hover:scale-105"
                onClick={() => window.open('https://n8n.rivillos.es/webhook/f46f5994-1c0d-46b4-b44a-ac9a6a4ef9e7/chat', '_blank')}
              >
                ¿Tienes dudas?
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-20 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Sobre <span className="text-emerald-500">Infinity Gym</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-zinc-300 leading-relaxed text-center">
              {gymInfo.philosophy}
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Target className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold">Profesionalidad</h3>
                <p className="text-zinc-400">Entrenadores certificados y equipamiento de élite</p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold">Comunidad</h3>
                <p className="text-zinc-400">Ambiente cercano y motivador para todos</p>
              </div>
              <div className="text-center space-y-3">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold">Excelencia</h3>
                <p className="text-zinc-400">Instalaciones modernas y siempre impecables</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Nuestros <span className="text-emerald-500">Servicios</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
            <p className="text-xl text-zinc-400 max-w-3xl mx-auto">Todo lo que necesitas para alcanzar tus objetivos en un solo lugar</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-zinc-900 border-zinc-800 hover:border-emerald-500/50 transition-all hover:scale-105 duration-300 cursor-pointer group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-500/30 transition-colors">
                      <IconComponent className="w-7 h-7 text-emerald-500" />
                    </div>
                    <CardTitle className="text-xl text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-zinc-400 text-base">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="ubicacion" className="py-20 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Nuestra <span className="text-emerald-500">Ubicación</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <MapPin className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Dirección</h3>
                  <p className="text-zinc-400">{location.address}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Clock className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Horario</h3>
                  <p className="text-zinc-400">{location.schedule}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Phone className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Teléfono</h3>
                  <p className="text-zinc-400">{location.phone}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Mail className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Email</h3>
                  <p className="text-zinc-400">{location.email}</p>
                </div>
              </div>
              <p className="text-zinc-400 pt-4">{location.description}</p>
            </div>
            <div className="w-full h-96 bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
              <iframe
                title="Mapa de ubicación"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.3971841469847!2d-3.3640681!3d40.4027626!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd424750e1b57dc7%3A0x98538aa485fcae8f!2sInfinity%20Gym%20S.L.!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galeria" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Nuestras <span className="text-emerald-500">Instalaciones</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
            <p className="text-xl text-zinc-400">Descubre el espacio donde alcanzarás tus metas</p>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-xl">
              <div className="relative h-96 md:h-[500px] bg-zinc-900">
                {galleryImages[currentGalleryIndex].src ? (
                  <>
                    {/* Fondo difuminado con la misma imagen */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ 
                        backgroundImage: `url(${galleryImages[currentGalleryIndex].src})`,
                        filter: 'blur(30px) brightness(0.4)',
                        transform: 'scale(1.1)'
                      }}
                    ></div>
                    {/* Imagen principal centrada y completa */}
                    <img 
                      src={galleryImages[currentGalleryIndex].src} 
                      alt={galleryImages[currentGalleryIndex].alt}
                      className="relative w-full h-full object-contain z-10"
                    />
                  </>
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${galleryImages[currentGalleryIndex].placeholder} flex items-center justify-center`}>
                    <p className="text-2xl text-zinc-500 font-semibold">{galleryImages[currentGalleryIndex].alt}</p>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={prevGalleryImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/80 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors z-20"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextGalleryImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/80 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors z-20"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="flex justify-center mt-6 space-x-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGalleryIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentGalleryIndex ? 'bg-emerald-500 w-8' : 'bg-zinc-600 hover:bg-zinc-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="py-20 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Lo que dicen <span className="text-emerald-500">nuestros miembros</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-8 h-8 ${i < Math.floor(gymRating.overall) ? 'fill-emerald-500 text-emerald-500' : 'text-zinc-600'}`} />
                ))}
              </div>
              <span className="text-3xl font-bold text-emerald-500">{gymRating.overall}</span>
              <span className="text-zinc-400">({gymRating.totalReviews} reseñas en Google)</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg text-white">{testimonial.name}</CardTitle>
                    <div className="flex space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-emerald-500 text-emerald-500" />
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400 italic">"{testimonial.comment}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifas" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Nuestras <span className="text-emerald-500">Tarifas</span></h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
            <p className="text-xl text-zinc-400">Elige el plan que mejor se adapte a ti</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`bg-zinc-900 border-zinc-800 relative ${
                  plan.popular ? 'border-emerald-500 shadow-lg shadow-emerald-500/20 scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white border-0">
                    Más popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-8">
                  <CardTitle className="text-2xl text-white mb-2">{plan.name}</CardTitle>
                  {plan.savings && (
                    <p className="text-sm text-emerald-500 font-semibold">{plan.savings}</p>
                  )}
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-emerald-500">{plan.price}€</span>
                    <span className="text-zinc-400">/{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-zinc-400 mt-8 text-lg">
            * Precios aplicables solo a musculación y cardio
          </p>

          {/* Mensaje informativo destacado */}
          <div className="mt-10 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-950/40 to-zinc-900 border border-emerald-500/30 rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center border border-emerald-500/40">
                    <Sparkles className="w-8 h-8 text-emerald-400" />
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  <h3 className="text-2xl font-bold text-white">
                    Gestión personalizada de planes
                  </h3>
                  <p className="text-zinc-300 leading-relaxed">
                    La contratación de todos los planes se gestiona <span className="text-emerald-400 font-semibold">directamente en el establecimiento</span>. 
                  </p>
                  <p className="text-zinc-300 leading-relaxed">
                    Si tienes dudas sobre precios o quieres más información, nuestra <span className="text-emerald-400 font-semibold">asistente IA</span> puede orientarte sobre las opciones disponibles antes de venir.
                  </p>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4 transition-all hover:scale-105"
                    onClick={() => window.open('https://n8n.rivillos.es/webhook/f46f5994-1c0d-46b4-b44a-ac9a6a4ef9e7/chat', '_blank')}
                  >
                    Consultar con la IA
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Bonos y opciones esporádicas */}
          <div className="mt-20">
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Bonos y <span className="text-emerald-500">opciones esporádicas</span>
              </h3>
              <div className="w-20 h-1 bg-emerald-500 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bonusOptions.map((bonus, index) => (
                <Card key={index} className="bg-zinc-900 border-zinc-800 hover:border-emerald-500/50 transition-all">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-lg font-semibold text-white">{bonus.name}</h4>
                      <span className="text-2xl font-bold text-emerald-500 whitespace-nowrap ml-3">{bonus.price}</span>
                    </div>
                    {bonus.detail && (
                      <p className="text-sm text-zinc-400">{bonus.detail}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Otros conceptos */}
          <div className="mt-16">
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Otros <span className="text-emerald-500">conceptos</span>
              </h3>
              <div className="w-20 h-1 bg-emerald-500 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {otherConcepts.map((concept, index) => (
                <div key={index} className="flex justify-between items-center bg-zinc-900 border border-zinc-800 rounded-lg px-6 py-4 hover:border-emerald-500/50 transition-all">
                  <span className="text-zinc-300">{concept.name}</span>
                  <span className="text-xl font-bold text-emerald-500">{concept.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <img src={logo} alt="Infinity Gym S.L." className="h-12 w-auto object-contain" />
              <p className="text-zinc-400 text-sm">
                Tu mejor versión empieza hoy. Únete a nuestra comunidad fitness.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Enlaces</h3>
              <ul className="space-y-2 text-zinc-400">
                <li><button onClick={() => scrollToSection('sobre')} className="hover:text-emerald-500 transition-colors">Sobre Nosotros</button></li>
                <li><button onClick={() => scrollToSection('servicios')} className="hover:text-emerald-500 transition-colors">Servicios</button></li>
                <li><button onClick={() => scrollToSection('tarifas')} className="hover:text-emerald-500 transition-colors">Tarifas</button></li>
                <li><a href="https://n8n.rivillos.es/webhook/f46f5994-1c0d-46b4-b44a-ac9a6a4ef9e7/chat" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-500 transition-colors">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Contacto</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-emerald-500" />
                  <span>{location.phone}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-emerald-500" />
                  <span>{location.email}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-emerald-500 mt-1" />
                  <span>{location.address}</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Síguenos</h3>
              <div className="flex space-x-4">
                {socialMedia.map((social) => {
                  const IconComponent = iconMap[social.icon];
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-zinc-800 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-zinc-500 text-sm">
              © 2024 {gymInfo.name}. Todos los derechos reservados.
            </p>
            <div className="flex space-x-6 text-sm text-zinc-500">
              <a href="#" className="hover:text-emerald-500 transition-colors">Política de Privacidad</a>
              <a href="#" className="hover:text-emerald-500 transition-colors">Aviso Legal</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;